import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import GuestAuditShell from './components/GuestAuditShell';
import ResearchListPage from './pages/ResearchListPage';
import ReportPage from './pages/ReportPage';
import KnowledgePage from './pages/KnowledgePage';
import PersonnelPage from './pages/PersonnelPage';
import PersonnelDetailPage from './pages/PersonnelDetailPage';
import OnchainPage from './pages/OnchainPage';
import FundraisingPage from './pages/FundraisingPage';
import TechnicalPage from './pages/TechnicalPage';
import StakingPage from './pages/StakingPage';
import FlowGraphPage from './pages/FlowGraphPage';
import NearFlowPage from './pages/NearFlowPage';
import AsterMonitorPage from './pages/AsterMonitorPage';
import HyperliquidMonitorPage from './pages/HyperliquidMonitorPage';
import ContractAuditPage from './pages/ContractAuditPage';
import EtfFlowPage from './pages/EtfFlowPage';
import NewsPage from './pages/NewsPage';
import DominancePage from './pages/DominancePage';
import PortfolioPage from './pages/PortfolioPage';
import GuidePage from './pages/GuidePage';
import LoginPage from './pages/LoginPage';
import { getToken, setToken, clearToken, getMe, getUser, setUser as storeUser } from './api';
import './App.css';

const HANDOFF_URL = 'https://trustflow.vn/vi/auth/handoff';
const PRICING_URL = 'https://trustflow.vn/vi/pricing';
// Set false to open the app as a free demo (no login/tier gating)
const AUTH_ENABLED = true;

// ── Capture the SSO token handed back by trustflow.vn (?token=<jwt>) ──────────
// Runs once at module load, before React renders, then strips it from the URL.
(function captureHandoffToken() {
  try {
    const search = new URLSearchParams(window.location.search);
    let token = search.get('token');
    if (!token && window.location.hash.includes('token=')) {
      const qi = window.location.hash.indexOf('?');
      if (qi >= 0) token = new URLSearchParams(window.location.hash.slice(qi + 1)).get('token');
    }
    if (token) {
      setToken(token);
      search.delete('token');
      const qs = search.toString();
      const clean = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
    }
  } catch { /* ignore */ }
})();

function CenteredMsg({ text }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>{text}</span>
    </div>
  );
}


// Trigger the SSO handoff (user-initiated — never an automatic full-page
// bounce, so an unauthenticated visitor is never stranded on trustflow.vn).
function goToHandoff() {
  const cb = encodeURIComponent(window.location.origin + window.location.pathname);
  window.location.href = `${HANDOFF_URL}?redirectTo=${cb}`;
}

// Routes anyone can open without logging in.
const PUBLIC_ROUTES = ['/contract-audit'];
const isPublicRoute = () => PUBLIC_ROUTES.some((p) => window.location.hash.replace(/^#/, '').split('?')[0].startsWith(p));

// Gate the whole app behind a TrustFlow login. Reads the JWT from our own
// storage OR the shared .trustflow.vn cookie, validates it, then requires an
// Advanced/Elite tier. Only shows ai.trustflow's own login (with an SSO
// button) when there is no valid session — it does NOT auto-redirect away.
function AuthGate({ children }) {
  const [status, setStatus] = useState('checking'); // 'checking' | 'authed' | 'login' | 'noaccess'
  const [, setUserState] = useState(null);

  const applyMe = (me) => {
    storeUser(me);
    setUserState(me);
    // Any authenticated user enters the app; per-page access for the
    // Advanced/Elite features is enforced by <TierGate> on every route.
    setStatus('authed');
  };

  useEffect(() => {
    if (!AUTH_ENABLED) { setStatus('authed'); return undefined; }
    let active = true;
    (async () => {
      // getToken() also reads the shared trustflow.vn cookie, so a user already
      // logged into trustflow.vn is authenticated here without any redirect
      // (once the shared-cookie change is live).
      if (getToken()) {
        try {
          const me = await getMe();
          if (active) { sessionStorage.removeItem('tf_sso_tried'); applyMe(me); }
          return;
        } catch { clearToken(); }
      }
      // Public pages (Contract Audit) work without an account: don't bounce a
      // visitor to the SSO handoff, just show the page in guest mode.
      if (isPublicRoute()) { if (active) setStatus('guest'); return; }
      // No local token yet → try the SSO handoff ONCE. If the user is logged
      // into trustflow.vn, it returns here with ?token= and logs us in.
      // The guard prevents a loop: if we come back still without a token, we
      // show ai.trustflow's own login instead of bouncing again.
      if (!sessionStorage.getItem('tf_sso_tried')) {
        sessionStorage.setItem('tf_sso_tried', '1');
        goToHandoff();
        return;
      }
      if (active) setStatus('login'); // stay on ai.trustflow, offer SSO/OTP
    })();
    return () => { active = false; };
  }, []);

  // Watch the shared SSO cookie while signed in: if it disappears (a logout on
  // trustflow.vn — or any TrustFlow property — deletes it), force a logout here
  // too. Checked on an interval and whenever the tab regains focus/visibility.
  useEffect(() => {
    if (!AUTH_ENABLED || status !== 'authed') return undefined;
    const check = () => {
      if (!getToken()) {
        clearToken();
        // Don't auto-bounce back to the handoff — the user is logged out.
        sessionStorage.setItem('tf_sso_tried', '1');
        setUserState(null);
        setStatus('login');
      }
    };
    const id = setInterval(check, 5000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [status]);

  if (status === 'checking') return <CenteredMsg text="Đang xác thực tài khoản TrustFlow..." />;
  if (status === 'guest') return <GuestAuditShell onSignIn={() => { sessionStorage.removeItem('tf_sso_tried'); goToHandoff(); }} />;
  if (status === 'login') {
    return (
      <LoginPage
        onLogin={async () => {
          try { applyMe(await getMe()); }
          catch { setStatus('login'); }
        }}
      />
    );
  }
  return children;
}

// Per-route gate for the Advanced/Elite-only app. Free users reaching any
// route see an inline upgrade prompt instead of the content.
function TierGate({ children }) {
  const user = getUser();
  if (user?.hasAccess) return children;
  const tierLabel = user?.tier ? user.tier.charAt(0).toUpperCase() + user.tier.slice(1) : 'Miễn phí';
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 24px' }}>
      <div style={{ maxWidth: 460, width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '36px 32px', textAlign: 'center', boxShadow: '0 4px 32px rgba(31,88,164,0.12)' }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>🔒</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>
          Tính năng dành cho gói Advanced & Elite
        </h2>
        <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
          Toàn bộ tính năng của TrustFlow Research dành riêng cho thành viên gói <strong>Advanced</strong> và <strong>Elite</strong>.
        </p>
        <p style={{ margin: '0 0 20px', fontSize: 12, color: 'var(--text-dim)' }}>
          Gói hiện tại: <strong>{tierLabel}</strong>
        </p>
        <a href={PRICING_URL} style={{ display: 'block', padding: '12px 0', borderRadius: 10, background: 'linear-gradient(135deg, #29a7df, #2657a5)', color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
          Nâng cấp gói tại trustflow.vn →
        </a>
      </div>
    </div>
  );
}

// Thin route wrappers bridging existing page props to router navigation
function PersonnelListRoute() {
  const navigate = useNavigate();
  return <PersonnelPage onViewDetail={slug => navigate(`/personnel/${slug}`)} />;
}
function PersonnelDetailRoute() {
  const { slug } = useParams();
  const navigate = useNavigate();
  return <PersonnelDetailPage slug={slug} onBack={() => navigate('/personnel')} />;
}

export default function App() {
  return (
    <AuthGate>
      <HashRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Navigate to="/technical" replace />} />
            {/* Advanced/Elite only */}
            <Route path="research" element={<TierGate><ResearchListPage /></TierGate>} />
            <Route path="research/:slug" element={<TierGate><ReportPage /></TierGate>} />
            <Route path="knowledge" element={<TierGate><KnowledgePage /></TierGate>} />
            <Route path="personnel" element={<TierGate><PersonnelListRoute /></TierGate>} />
            <Route path="personnel/:slug" element={<TierGate><PersonnelDetailRoute /></TierGate>} />
            <Route path="onchain" element={<TierGate><OnchainPage /></TierGate>} />
            <Route path="staking" element={<TierGate><StakingPage /></TierGate>} />
            <Route path="flow-graph" element={<TierGate><FlowGraphPage /></TierGate>} />
            <Route path="hack-tracker" element={<Navigate to="/flow-graph" replace />} />
            <Route path="near-flow" element={<TierGate><NearFlowPage /></TierGate>} />
            <Route path="aster-monitor" element={<TierGate><AsterMonitorPage /></TierGate>} />
            <Route path="hyperliquid-monitor" element={<TierGate><HyperliquidMonitorPage /></TierGate>} />
            {/* Open to every logged-in user, free tier included. */}
            <Route path="contract-audit" element={<ContractAuditPage />} />
            <Route path="etf-flow" element={<TierGate><EtfFlowPage /></TierGate>} />
            <Route path="news" element={<TierGate><NewsPage /></TierGate>} />
            <Route path="dominance" element={<TierGate><DominancePage /></TierGate>} />
            <Route path="portfolio" element={<TierGate><PortfolioPage /></TierGate>} />
            <Route path="fundraising" element={<TierGate><FundraisingPage /></TierGate>} />
            <Route path="technical" element={<TierGate><TechnicalPage /></TierGate>} />
            {/* Accessible regardless of tier — a free user deciding whether
                to upgrade needs to be able to read what each feature does. */}
            <Route path="guide" element={<GuidePage />} />
            <Route path="*" element={<Navigate to="/technical" replace />} />
          </Route>
        </Routes>
      </HashRouter>
    </AuthGate>
  );
}
